import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, CloudCheck, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const IMAGES = [
  "apply-intro.webp", "brand-ambulation.webp", "brand-cards.webp", "brand-companionship.webp",
  "brand-cooking.webp", "brand-daily-care.webp", "brand-gardening.webp", "brand-housekeeping.webp",
  "brand-laundry.webp", "brand-orientation.webp", "brand-personal-care.webp", "brand-reading.webp",
  "brand-tea.webp", "brand-transport.webp", "brand-walk.webp", "cool-bluehour-arrival.webp",
  "cool-dusk-evv.webp", "cool-golden-window.webp", "cool-lamp-reading.webp", "cool-morning-tea.webp",
  "cool-sunflare-garden.webp", "goose-bingo.webp", "goose-healthfair.webp", "goose-photoop.webp",
  "goose-teaparty.webp", "orient-dress-code.webp", "orient-exam.webp", "orient-handbook.webp",
  "orient-rights.webp", "orient-safety.webp", "orient-welcome.webp", "purple-comfort.webp",
  "purple-hair.webp", "purple-serving.webp", "purple-stretch.webp", "purple-walker.webp",
  "purple-wheelchair.webp", "scene-birthday.webp", "scene-dance.webp", "scene-duckpond.webp",
  "scene-mail.webp", "scene-manicure.webp", "scene-photoalbum.webp", "scene-recipe.webp",
  "scene-videocall.webp", "shc-ambulation.webp", "shc-badge.webp", "shc-cards.webp",
  "shc-cards-2.webp", "shc-china-cabinet.webp", "shc-companionship.webp", "shc-companionship-2.webp",
  "shc-curlers.webp", "shc-door.webp", "shc-door-greet.webp", "shc-dressing.webp",
  "shc-dressing-2.webp", "shc-evv-phone.webp", "shc-footcare.webp", "shc-gen-baking.webp",
  "shc-gen-documentation.webp", "shc-gen-dressing.webp", "shc-gen-errands.webp",
  "shc-gen-exercise.webp", "shc-gen-family-welcome.webp", "shc-gen-garden.webp",
  "shc-gen-laundry.webp", "shc-gen-meds.webp", "shc-gen-pet.webp", "shc-gen-puzzle.webp",
  "shc-gen-shower.webp", "shc-gen-steps.webp", "shc-gen-training-room.webp", "shc-gen-transfer.webp",
  "shc-gen-winter.webp", "shc-grooming.webp", "shc-hero.webp", "shc-housekeeping.webp",
  "shc-housekeeping-2.webp", "shc-meal-1.webp", "shc-meal-2.webp", "shc-meal-3.webp",
  "shc-meal-plates.webp", "shc-medication.webp", "shc-personal-3.webp", "shc-personal-care.webp",
  "shc-personal-care-2.webp", "shc-soup.webp", "shc-transportation.webp", "shc-walker.webp",
  "shc-wiping.webp", "site-cards.webp", "site-care.webp", "site-meal.webp",
];

const SEED = {
  "scene-manicure.webp": "yes", "shc-curlers.webp": "yes", "purple-comfort.webp": "yes",
  "site-care.webp": "yes", "site-cards.webp": "yes", "site-meal.webp": "yes",
};

const SLOTS = [
  ["", "Where to use it…"],
  ["homepage-hero", "Homepage hero — cycles if you pick several"],
  ["homepage-about", "Homepage — About section"],
  ["apply-page", "Caregivers apply page"],
  ["handbook", "Client handbook"],
  ["training", "Staff training"],
  ["service-personal-care-hygiene", "Services — Personal Care & Hygiene"],
  ["service-companionship-social-engagement", "Services — Companionship & Social Engagement"],
  ["service-light-housekeeping", "Services — Light Housekeeping"],
  ["service-meal-preparation", "Services — Meal Preparation"],
  ["service-medication-reminders", "Services — Medication Reminders"],
  ["service-transportation-assistance", "Services — Transportation Assistance"],
  ["service-respite-care-for-family-caregivers", "Services — Respite Care"],
  ["service-24-hour-awake-care", "Services — 24-Hour Awake Care"],
  ["other", "Somewhere else"],
];

export default function ImageGalleryPage() {
  const [choices, setChoices] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      let local = {};
      try { local = JSON.parse(localStorage.getItem("shc-image-votes") || "{}"); } catch { /* ignore */ }
      let server = {};
      try { ({ data: { choices: server = {} } } = await api.get("/image-choices")); } catch { /* ignore */ }
      const merged = {};
      for (const n of IMAGES) {
        const s = server[n] || {};
        merged[n] = { vote: s.vote || local[n] || SEED[n] || null, use: s.use || "" };
      }
      setChoices(merged);
    })();
  }, []);

  useEffect(() => {
    if (!choices) return;
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put("/image-choices", { choices });
        localStorage.setItem("shc-image-votes", JSON.stringify(
          Object.fromEntries(Object.entries(choices).map(([k, v]) => [k, v.vote]).filter(([, v]) => v))));
        setSaveState("saved");
      } catch {
        setSaveState("saved");
        toast.error("Couldn't save — your picks are still on this device");
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [choices]);

  const setVote = (name, v) =>
    setChoices((prev) => ({ ...prev, [name]: { ...prev[name], vote: prev[name].vote === v ? null : v } }));
  const setUse = (name, use) =>
    setChoices((prev) => ({ ...prev, [name]: { ...prev[name], use } }));

  const counts = useMemo(() => {
    if (!choices) return { yes: 0, no: 0, left: IMAGES.length, assigned: 0 };
    const vals = IMAGES.map((n) => choices[n] || {});
    return {
      yes: vals.filter((v) => v.vote === "yes").length,
      no: vals.filter((v) => v.vote === "no").length,
      left: vals.filter((v) => !v.vote).length,
      assigned: vals.filter((v) => v.use).length,
    };
  }, [choices]);

  const slotSummary = useMemo(() => {
    if (!choices) return [];
    const by = {};
    for (const n of IMAGES) {
      const c = choices[n];
      if (c?.vote === "yes" && c?.use) (by[c.use] = by[c.use] || []).push(n);
    }
    return Object.entries(by);
  }, [choices]);

  if (!choices) return <div className="flex min-h-screen items-center justify-center bg-stone-100"><CircleNotch size={32} className="animate-spin text-stone-400" /></div>;

  return (
    <div className="min-h-screen bg-stone-100" data-testid="image-gallery-page">
      <header className="sticky top-0 z-10 border-b border-stone-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div>
            <h1 className="font-heading text-xl font-semibold text-[#23282f]">Site image picker</h1>
            <p className="text-sm text-stone-500">
              Rule: if it clashes with grey and purple, it's a no.{" "}
              <span className="font-semibold text-emerald-700">{counts.yes} work</span> ·{" "}
              <span className="font-semibold text-red-700">{counts.no} don't</span> ·{" "}
              {counts.left} undecided ·{" "}
              <span className="font-semibold text-[#6A3E97]">{counts.assigned} assigned</span>
            </p>
          </div>
          <p className="flex items-center gap-1.5 text-xs font-medium text-stone-500" data-testid="save-state">
            {saveState === "saving" ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : <><CloudCheck size={14} className="text-emerald-600" /> Saved — the site can see these</>}
          </p>
        </div>
        {slotSummary.length > 0 && (
          <div className="border-t border-stone-100 bg-[#F5F2FA]" data-testid="slot-summary">
            <div className="mx-auto flex max-w-7xl flex-wrap gap-x-6 gap-y-1 px-5 py-2 text-xs text-stone-600">
              {slotSummary.map(([slot, names]) => (
                <span key={slot}><span className="font-semibold text-[#6A3E97]">{SLOTS.find(([k]) => k === slot)?.[1] || slot}:</span> {names.length} image{names.length > 1 ? "s" : ""}{slot === "homepage-hero" && names.length > 1 ? " (cycling)" : ""}</span>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8">
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {IMAGES.map((name) => {
            const c = choices[name] || {};
            const v = c.vote;
            return (
              <div key={name} data-testid={`gallery-card-${name.replace(".webp", "")}`}
                className={`overflow-hidden rounded-xl border-2 bg-white transition-colors ${
                  v === "yes" ? "border-emerald-500" : v === "no" ? "border-red-400" : "border-transparent"
                }`}>
                <img src={`/${name}`} alt={name} className="aspect-square w-full object-cover" loading="lazy" />
                <div className="p-3">
                  <p className="truncate text-xs font-medium text-stone-600" title={name}>{name}</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => setVote(name, "yes")} data-testid={`vote-yes-${name.replace(".webp", "")}`}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        v === "yes" ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-emerald-50 hover:text-emerald-700"
                      }`}>
                      <Check size={15} weight="bold" /> Works
                    </button>
                    <button onClick={() => setVote(name, "no")} data-testid={`vote-no-${name.replace(".webp", "")}`}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        v === "no" ? "bg-red-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-red-50 hover:text-red-600"
                      }`}>
                      <X size={15} weight="bold" /> No
                    </button>
                  </div>
                  <select value={c.use || ""} onChange={(e) => setUse(name, e.target.value)}
                    data-testid={`use-${name.replace(".webp", "")}`}
                    className={`mt-2 w-full rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                      c.use ? "border-[#6A3E97] bg-[#F5F2FA] text-[#4A235A]" : "border-stone-200 bg-white text-stone-500"
                    }`}>
                    {SLOTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
